import { formatDuration } from 'date-fns'
import { type DynamicHeroValues } from 'libs/dynamic-hero'
import { type PrunedCourse } from 'libs/server/api'

/**
 * Personalization is all or nothing: the description is a MUST, so imagery
 * must never render over the course's own copy.
 */
export const personalizedHero = (savedDynamicHero: DynamicHeroValues) =>
  savedDynamicHero?.subheader ? savedDynamicHero : undefined

/**
 * Base function that returns the chapters data:
 * - sortedSubchapters if they are present (for continuous cut courses)
 * - sortedChapters otherwise (for regular courses)
 */
export const getChaptersData = (course: PrunedCourse) => {
  const { sortedSubchapters, sortedChapters } = course

  const subchaptersCount = sortedSubchapters?.length
  const chapters = subchaptersCount ? sortedSubchapters : sortedChapters ?? []

  return {
    chapters,
    chaptersCount: chapters.length,
    subchaptersCount,
  }
}

export const getEpisodeListFromCourse = (course: PrunedCourse) => {
  const { upcomingChapter } = course
  const { chapters } = getChaptersData(course)

  return upcomingChapter ? [...chapters, upcomingChapter] : chapters
}

const getCourseTime = (
  chapters: ReturnType<typeof getChaptersInfo>['chapters'],
  subchaptersCount: number,
) => {
  let totalTimeSeconds = 0

  if (subchaptersCount) {
    totalTimeSeconds = chapters.reduce((acum, chapter) => {
      if (
        !chapter.duration ||
        !('startTime' in chapter) ||
        !('endTime' in chapter)
      )
        return acum
      // For subchapters, duration is endTime - startTime
      return acum + (chapter.endTime - chapter.startTime)
    }, 0)
  } else {
    // Loop through each chapter duration
    for (let i = chapters.length - 1; i >= 0; i -= 1) {
      if (!chapters[i].duration) break

      const durationSplit = String(chapters[i].duration).split(':')
      let hours = '0'
      if (durationSplit.length === 3) {
        hours = durationSplit.shift() || '0'
      }
      const [minutes, seconds] = durationSplit
      totalTimeSeconds += parseInt(hours, 10) * 60 * 60
      totalTimeSeconds += parseInt(minutes, 10) * 60
      totalTimeSeconds += parseInt(seconds, 10)
    }
  }

  const duration = {
    hours: Math.floor(totalTimeSeconds / 60 / 60),
    minutes: Math.floor(totalTimeSeconds / 60) % 60,
  }

  // Custom locale for abbreviated format
  const customLocale = {
    formatDistance: (token: string, count: number) => {
      const formatDistanceLocale: Record<string, string> = {
        xHours: count === 1 ? '1hr' : `${count}hr`,
        xMinutes: count === 1 ? '1mins' : `${count}mins`,
      }
      return formatDistanceLocale[token] || ''
    },
  }

  return formatDuration(duration, {
    format: ['hours', 'minutes'],
    locale: customLocale,
  })
}

const getChaptersInfo = (course: PrunedCourse) => {
  const { chapters, chaptersCount, subchaptersCount } = getChaptersData(course)

  return {
    chapters,
    chaptersCount,
    subchaptersCount,
  }
}

// Must match the text painted on the nameplate image: the instructor for
// single-instructor classes, the skill for multi-instructor ones.
export const getNameplateLabel = (
  course: Pick<PrunedCourse, 'displayTitle' | 'title' | 'instructorName'>,
) => course.displayTitle || course.title || course.instructorName || ''

export const getChaptersAndDuration = (course: PrunedCourse) => {
  const { chapters, chaptersCount, subchaptersCount } = getChaptersInfo(course)
  return {
    chaptersCount,
    courseDuration: getCourseTime(chapters, subchaptersCount),
  }
}

export const formatSeconds = (totalSeconds: number): string => {
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60

  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }
  return `${m}:${s.toString().padStart(2, '0')}`
}

export const formatChapterDuration = (
  startTime: number | null | undefined,
  endTime: number | null | undefined,
): string => {
  if (!startTime || !endTime) return '00:00'
  const seconds = endTime - startTime

  const h = Math.floor(Number(seconds) / 3600)
  const m = Math.floor((Number(seconds) % 3600) / 60)
  const s = Number(seconds) % 60

  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }
  return `${m}:${s.toString().padStart(2, '0')}`
}
